import React, { useContext } from 'react';
import { BuilderComponent } from '@builder.io/react';
import classNames from 'classnames';
import { useCmsContentQuery, useCmsMenuQuery } from '@/api/cms/cms';
import { Link, Logo } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { LayoutResponse, TranslationsWebsite } from '@/generated/data.schemas';
import { useGetSettings } from '@/generated/settings';
import { BuilderDependenciesProvider } from '../cms/contexts/BuilderDependenciesProvider';
import styles from './Footer.module.scss';
import { Menu } from './Menu';

interface FooterProps {
	critical?: boolean;
	layout: LayoutResponse | undefined;
	translations: TranslationsWebsite | undefined;
}

const Footer: React.FunctionComponent<FooterProps> = ({ critical, layout, translations }) => {
	const frontPageUrl = layout?.frontPageUrl;
	const { segmentationId } = useContext(SegmentationContext);
	const { data: settings } = useGetSettings(segmentationId);
	const { data: footerMenu } = useCmsMenuQuery(settings?.data, settings?.data.cmsSettings.footerMenu || '');

	const modelName = settings?.data.cmsSettings.footerContact || '';
	const { data: footerContact } = useCmsContentQuery({
		settings: settings?.data,
		modelName,
	});

	return (
		<BuilderDependenciesProvider>
			<footer className={classNames(styles.wrapper, { [styles.isCritical]: critical })}>
				{!critical && (
					<nav
						className={classNames(styles.menuNav, 'u-container-lg')}
						aria-label={translations?.shared.navigation.footer}
					>
						<div className={classNames(styles.menuCol, styles.isContact)}>
							<Link
								to={frontPageUrl || '/'}
								color={'default'}
								title={translations?.shared.siteName}
								hasMinHeight
							>
								<Logo name={segmentationId === 1 || segmentationId === 2 ?  'ccj' : 'casting'} />
							</Link>
							{footerContact && (
								<BuilderComponent
									model="footer-contact"
									content={footerContact}
									locale={settings?.data.cultureCode}
								/>
							)}
						</div>

						{footerMenu && <Menu menuColClassName={styles.menuCol} menu={footerMenu} />}
					</nav>
				)}

				<section className={styles.criticalWrapper}>
					<div className={classNames(styles.criticalInnerWrapper, 'u-container-lg')}>
						<p className={styles.copyright}>{translations?.footer.copyright}</p>
					</div>
				</section>
			</footer>
		</BuilderDependenciesProvider>
	);
};

export const MemoizedFooter = React.memo(Footer);
